import React, { Component } from "react";
import clsx from "clsx";

import { I } from "./common/icons";
import { STATUSES, STATUS_ORDER, FREQUENCIES, ACCOUNT_TYPES, ROLE_ORDER, ROLES } from "./backofficeConstants";
import { fmt, isOverdue, isComplete, budgetTotal, loggedTotal, hrs, userName, userShort } from "./backofficeUtils";

/**
 * One backoffice task: checkbox, name, budget chip, email button, dates, status, P/R/M chips,
 * edit/delete, and the tracked-accounts block when the task has accounts.
 */
class TaskRow extends Component {
	state = { newAccount: "", newAccountType: "bank", popping: false };

	onToggle = () => {
		this.setState({ popping: true });
		setTimeout(() => this.setState({ popping: false }), 400);
		this.props.onToggleComplete(this.props.task);
	};

	addAccount = () => {
		const name = this.state.newAccount.trim();
		if (!name) return;
		this.props.onAddAccount(this.props.task, { name, type: this.state.newAccountType });
		this.setState({ newAccount: "" });
	};

	renderBudgetChip(task) {
		const b = budgetTotal(task);
		const l = loggedTotal(task);
		if (!b && !l) return null;
		const cls = l > b ? "bo-budchip--over" : !isComplete(task) && b && l >= b * 0.9 ? "bo-budchip--near" : "";
		const title = `Time budget · P ${hrs(task.budget.preparer)}h / R ${hrs(task.budget.reviewer)}h / M ${hrs(task.budget.manager)}h — logged ${hrs(l)}h`;
		return (
			<span className={clsx("bo-budchip", cls)} title={title}>
				<I.clock />
				{hrs(l)} / {hrs(b)}h
			</span>
		);
	}

	renderAccounts(task) {
		const { accountsOpen, onToggleAccounts, onToggleAccount, onRemoveAccount } = this.props;
		const n = task.accounts.length;
		const d = task.accounts.filter(a => a.isDone).length;
		return (
			<div className={clsx("bo-accts", { "bo-accts--open": accountsOpen })}>
				<button className="bo-accts__head" type="button" onClick={() => onToggleAccounts(task)} aria-expanded={accountsOpen}>
					<I.card />
					<span>Accounts</span>
					<span className="bo-accts__minibar">
						<i style={{ width: `${n ? Math.round((d / n) * 100) : 0}%` }} />
					</span>
					<span>
						{d} / {n} done
					</span>
					<span className="bo-accts__right">{accountsOpen ? "Hide" : "Show"}</span>
					<I.chev className={clsx("bo-chev", { "bo-chev--open": accountsOpen })} />
				</button>
				<div className="bo-accts__body">
					<div className="bo-accts__inner">
						{task.accounts.map(a => (
							<div key={a.id} className={clsx("bo-arow", { "bo-arow--done": a.isDone })}>
								<button className={clsx("bo-cb", { "bo-cb--on": a.isDone })} type="button" onClick={() => onToggleAccount(task, a)} aria-label={`Mark ${a.name} done`}>
									{a.isDone && <I.check />}
								</button>
								<span className="bo-arow__name">
									{a.name}
									{a.glCode && <small>GL {a.glCode}</small>}
								</span>
								<span className={clsx("bo-atype", `bo-atype--${a.type}`)}>{ACCOUNT_TYPES[a.type] || a.type}</span>
								<span className={clsx("bo-arec", { "bo-arec--ok": a.isDone })}>{a.isDone ? "Done" : "Open"}</span>
								<button className="bo-ico bo-ico--del" type="button" onClick={() => onRemoveAccount(task, a)} aria-label={`Remove ${a.name}`}>
									<I.trash />
								</button>
							</div>
						))}
						<div className="bo-aadd">
							<input
								type="text"
								value={this.state.newAccount}
								onChange={e => this.setState({ newAccount: e.target.value })}
								onKeyDown={e => e.key === "Enter" && this.addAccount()}
								placeholder="Add an account, e.g. RBC US Visa ••2231"
								aria-label="New account name"
							/>
							<select value={this.state.newAccountType} onChange={e => this.setState({ newAccountType: e.target.value })} aria-label="Account type">
								{Object.keys(ACCOUNT_TYPES).map(k => (
									<option key={k} value={k}>
										{ACCOUNT_TYPES[k]}
									</option>
								))}
							</select>
							<button className="bo-btn bo-btn--sm" type="button" onClick={this.addAccount}>
								<I.plus />
								Add account
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	render() {
		const { task, users, flash, onStatusChange, onEdit, onDelete, onOpenEmail } = this.props;
		const od = isOverdue(task);
		const done = isComplete(task);
		const st = STATUSES[task.status] || STATUSES.not_started;

		return (
			<div className={clsx("bo-trow", `bo-trow--${st.cls}`, { "bo-trow--overdue": od, "bo-trow--done": done, "bo-trow--flash": flash })} data-task={task.id}>
				<div className="bo-tmain">
					<button className={clsx("bo-cb", "ft-bo-task-check", { "bo-cb--on": done, "bo-cb--pop": this.state.popping })} type="button" onClick={this.onToggle} aria-label="Mark complete">
						{done && <I.check />}
					</button>
					<div className="bo-tname">
						<span className={clsx({ "bo-tname__link": task.isClientLink })}>{task.title}</span>
						{task.hint && <span className="bo-tname__hint">({task.hint})</span>}
						{this.renderBudgetChip(task)}
					</div>
					{task.description && <div className="bo-tdesc">{task.description}</div>}
				</div>
				<div className="bo-tctl">
					{task.emailAutomation && (
						<button className="bo-btn bo-btn--sm bo-btn--email ft-bo-task-email" type="button" onClick={() => onOpenEmail(task)}>
							<I.mail />
							Automated Email
						</button>
					)}
					<span className={clsx("bo-datechip", { "bo-datechip--overdue": od })}>
						{task.frequency !== "one_time" && <I.repeat />}
						Start: {fmt(task.startDate)} • Due: {fmt(task.dueDate)} ({FREQUENCIES[task.frequency] || task.frequency})
					</span>
					<select className={clsx("bo-status", "ft-bo-task-status", od ? "bo-status--overdue" : `bo-status--${st.cls}`)} value={task.status} onChange={e => onStatusChange(task, e.target.value)} aria-label="Status">
						{STATUS_ORDER.map(s => (
							<option key={s} value={s}>
								{STATUSES[s].label}
							</option>
						))}
					</select>
					<span className="bo-roles">
						{ROLE_ORDER.map(r => {
							const role = ROLES[r];
							const id = task[role.idField];
							return (
								<span key={r} className={clsx("bo-role", `bo-role--${role.cls}`)} title={`${role.label}: ${userName(users, id) || "Unassigned"}`}>
									{role.short}: {userShort(users, id)}
								</span>
							);
						})}
					</span>
				</div>
				<div className="bo-tacts">
					<button className="bo-ico ft-bo-task-edit" type="button" onClick={() => onEdit(task)} aria-label="Edit">
						<I.edit />
					</button>
					<button className="bo-ico bo-ico--del ft-bo-task-delete" type="button" onClick={() => onDelete(task)} aria-label="Delete">
						<I.trash />
					</button>
				</div>
				{task.accounts && task.accounts.length > 0 && this.renderAccounts(task)}
			</div>
		);
	}
}

export default TaskRow;
